/* eslint-disable */

import { ChartOptions, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { htmlLegendPlugin } from 'common/utils/chart';
import KioskChartLegends from 'extends/KioskChartLegends';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSeparators } from 'common/utils/functions';

interface CardiovascularRiskProps {
  cardiovascularRisk?: {
    veryHigh: number;
    high: number;
    lowAndMedium: number;
    missingHeartRisk: number;
  };
  style?: React.CSSProperties;
}

const CardiovascularRisk: React.FC<CardiovascularRiskProps> = ({ cardiovascularRisk, style }) => {
  const { t } = useTranslation();

  const handlLabelName = (label?: string) => {
    switch (label) {
      case 'veryHigh':
        return t('dashboard.veryHigh');
      case 'high':
        return t('dashboard.high');
      case 'lowAndMedium':
        return t('dashboard.lowAndMedium');
      case 'missingHeartRisk':
        return t('dashboard.noAssess');
      default:
        return '';
    }
  };

  const useMemoCardiovascularRiskData = useMemo(() => {
    if (!cardiovascularRisk) {
      return {
        total: [],
        cardiovascularRisk: []
      };
    }
    const cardiovascularRiskData = Object.values(cardiovascularRisk);
    const total = cardiovascularRiskData.reduce((acc, curr) => acc + curr, 0);
    return {
      total: cardiovascularRiskData,
      cardiovascularRisk: Object.keys(cardiovascularRisk).map((key) => handlLabelName(key)),
      percentage: cardiovascularRiskData.map((item) => ((item / total) * 100).toFixed(2)),
    };
  }, [cardiovascularRisk]);

  const data = {
    datasets: [
      {
        data: useMemoCardiovascularRiskData.total,
        backgroundColor: ['#5C1E3F', '#8B2D5F', '#B93C7F', '#D274A6', '#E6B2CE'],
        borderWidth: 0,
      }
    ],
    labels: useMemoCardiovascularRiskData.cardiovascularRisk,
  };

  const chartRef = useRef<any>(null);
  const [hiddens, setHiddens] = useState<boolean[]>(data.labels.map(() => false));

  const options: ChartOptions<'doughnut'> = useMemo(() => ({
    responsive: true,
    cutout: '0%',
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 16,
          weight: 600
        },
        formatter: (value: number, context) => {
          if (value === 0) return '';
          const percentage = Math.round(Number(useMemoCardiovascularRiskData.percentage?.[context.dataIndex]));
          return [percentage > 2 ? `${percentage}%` : ''];
        }
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: TooltipItem<'doughnut'>) => {
            const percentage = useMemoCardiovascularRiskData.percentage?.[tooltipItem.dataIndex];
            return `${tooltipItem.label}: ${formatNumberWithSeparators(Number(tooltipItem.raw))} (${Math.round(Number(percentage))}%)`;
          },
        },
      },
    }
  }), [useMemoCardiovascularRiskData]);

  const handleLegendClick = (index: number) => {
    const chart = chartRef.current;

    if (!chart) return;

    // Toggle visibility
    chart.toggleDataVisibility(index);
    chart.update();

    // Update local state (to dim legend item)
    setHiddens((prev) => {
      const newHidden = [...prev];
      newHidden[index] = !newHidden[index];
      return newHidden;
    });
  };

  return (
    <div
      className='p-home_chart'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        ...style
      }}
    >
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#3D424F',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        {t('dashboard2025.heartGroup')}
      </h1>
      <div style={{ width: '366px' }}>
        <Doughnut
          ref={chartRef}
          data={data}
          options={options}
          plugins={[ChartDataLabels, htmlLegendPlugin()]}
        />
      </div>
      <KioskChartLegends
        hiddens={hiddens}
        style={{ marginTop: 20 }}
        labels={data.labels as string[]}
        colors={data.datasets[0].backgroundColor as string[]}
        onClick={handleLegendClick}
      />
    </div>
  );
};

export default CardiovascularRisk;
